import { Technician } from '../../types/technician-types';

export interface TechnicianStageRef {
  technicianId?: string | null;
  technicianName?: string | null;
}

export interface InactiveTechnicianLookup {
  inactiveIds: Set<string>;
  inactiveNames: Set<string>;
}

export function normalizeTechnicianName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Derives inactive technician ids/names from completed stages on a truss so that
 * legacy rows missing technicianId still match siblings that have the id stored.
 */
export function buildInactiveTechnicianLookup(
  stages: TechnicianStageRef[],
  activeTechnicians: Technician[]
): InactiveTechnicianLookup {
  const activeIds = new Set(activeTechnicians.map(t => String(t.id)));
  const inactiveIds = new Set<string>();
  const inactiveNames = new Set<string>();

  for (const stage of stages) {
    if (!stage.technicianId) continue;

    const id = String(stage.technicianId);
    if (activeIds.has(id)) continue;

    inactiveIds.add(id);
    if (stage.technicianName) {
      inactiveNames.add(normalizeTechnicianName(stage.technicianName));
    }
  }

  return { inactiveIds, inactiveNames };
}

export function isTechnicianInactive(
  technicianId: string | null | undefined,
  technicianName: string | null | undefined,
  activeTechnicians: Technician[],
  inactiveLookup: InactiveTechnicianLookup
): boolean {
  if (technicianId) {
    const id = String(technicianId);
    if (activeTechnicians.some(t => String(t.id) === id)) return false;
    return true;
  }

  if (technicianName && inactiveLookup.inactiveNames.has(normalizeTechnicianName(technicianName))) {
    return true;
  }

  return false;
}

/**
 * Resolves the technician label shown on completed production stages.
 * Uses snapshotted names when available and applies a consistent (Inactive) suffix
 * across all sign-offs for the same truss.
 */
export function getTechnicianDisplayName(
  technicianId: string | null | undefined,
  technicianName: string | null | undefined,
  activeTechnicians: Technician[],
  inactiveLookup: InactiveTechnicianLookup = { inactiveIds: new Set(), inactiveNames: new Set() }
): string {
  if (!technicianId && !technicianName) return '';

  const activeTech = technicianId
    ? activeTechnicians.find(t => String(t.id) === String(technicianId))
    : undefined;
  if (activeTech) {
    return `${activeTech.firstName} ${activeTech.lastName}`;
  }

  const name = technicianName?.trim() || 'Inactive Technician';
  if (isTechnicianInactive(technicianId, technicianName, activeTechnicians, inactiveLookup)) {
    return `${name} (Inactive)`;
  }

  return name;
}
